import { useEffect, useState, useMemo } from 'react';
import { Package, ChevronDown, Building2, GitBranch, GitMerge, BookOpen, X, FileDown, RotateCcw, Target, Layers, MousePointer, BookMarked, Database, Brain, Clock } from 'lucide-react';
import { getCatalogueClients, getCatalogueForClient, type CatalogueClient, type CatalogueGroup, type CatalogueAsset } from '../services/catalogueActifsService';
import CatalogueActifsSection from '../components/CatalogueActifsSection';
import { generatePDF } from '../services/pdfGenerator';
import { useLexLastSync } from '../contexts/LexLastSyncContext';
import { formatLastAnalysisDate } from '../utils/dateFormatter';

type ViewMode = 'enfant' | 'parent';

export default function CatalogueActifs() {
  const [clients, setClients] = useState<CatalogueClient[]>([]);
  const [groups, setGroups] = useState<CatalogueGroup[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('enfant');
  const [isClientDropdownOpen, setIsClientDropdownOpen] = useState(false);
  const [isGuideOpen, setIsGuideOpen] = useState(false);
  const [guideTab, setGuideTab] = useState<'vision' | 'servo' | 'actif' | 'usage' | 'cadres'>('vision');

  const { dates: lexDates } = useLexLastSync();
  const catalogueActifsSyncDate = isGuideOpen ? (lexDates['catalogue_actifs_last_ai_analysis'] ?? null) : null;

  // Charger les clients catalogue au montage
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setIsLoading(true);
        setError(null);
        const list = await getCatalogueClients();
        if (cancelled) return;
        setClients(list);
        if (list.length > 0) {
          setSelectedClientId(list[0].id);
        }
      } catch (e: any) {
        if (cancelled) return;
        console.error('Erreur chargement clients catalogue:', e);
        setError(e.message || 'Erreur chargement clients');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Charger le catalogue pour le client sélectionné
  useEffect(() => {
    if (!selectedClientId) {
      setGroups([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setIsLoading(true);
        setError(null);
        const catalogueGroups = await getCatalogueForClient(selectedClientId);
        if (cancelled) return;
        setGroups(catalogueGroups);
        setSelectedAssetId(null);
      } catch (e: any) {
        if (cancelled) return;
        console.error('Erreur chargement catalogue actifs:', e);
        setError(e.message || 'Erreur chargement catalogue');
        setGroups([]);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedClientId]);

  const allAssets: CatalogueAsset[] = useMemo(
    () => groups.flatMap((g) => g.assets || []),
    [groups]
  );

  const highlightedAssetIds = useMemo(() => {
    if (!selectedAssetId) return new Set<string>();
    const selectedAsset = allAssets.find((a) => a.id === selectedAssetId);
    if (!selectedAsset) return new Set<string>();
    // Vue Enfant = « ce dont ça dépend » → parents
    // Vue Parent = « ce qui en dépend »   → enfants
    return viewMode === 'enfant'
      ? new Set(selectedAsset.parentDependencies)
      : new Set(selectedAsset.childDependencies);
  }, [selectedAssetId, viewMode, allAssets]);

  const handleAssetClick = (assetId: string) => {
    setSelectedAssetId(selectedAssetId === assetId ? null : assetId);
  };

  const handleClientChange = (clientId: string) => {
    setSelectedClientId(clientId);
    setSelectedAssetId(null);
    setIsClientDropdownOpen(false);
  };

  const handleResetSelection = () => {
    setSelectedAssetId(null);
  };

  const handleExportPdf = async () => {
    const safeName = (selectedClient?.name ?? 'Client').replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_-]/g, '');
    const filename = `Catalogue_actifs_${safeName}_${new Date().toISOString().split('T')[0]}.pdf`;
    await generatePDF('catalogue-actifs-pdf', filename, { format: 'a4', orientation: 'landscape', fit: 'width' });
  };

  const selectedClient = selectedClientId ? clients.find((c) => c.id === selectedClientId) : null;

  return (
    <div id="catalogue-actifs-pdf" className="h-full flex flex-col bg-slate-50/80">
      {/* Header compact et lisible */}
      <header className="shrink-0 bg-white border-b border-slate-200/80 shadow-sm">
        <div className="px-6 py-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-100 text-indigo-600">
                <Package className="h-5 w-5" />
              </div>
              <div>
                <h1 className="text-xl font-semibold text-slate-800 tracking-tight">
                  Catalogue des actifs
                </h1>
                <p className="text-xs text-slate-500 mt-0.5">
                  Impact et dépendances — vue rapide
                </p>
              </div>
            </div>

            {/* Légende — couleurs plus saturées, différenciées Enfant / Parent */}
            <div className="flex items-center gap-4 text-xs text-slate-600">
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-flex h-4 w-4 rounded border-2 border-slate-300 bg-white shadow-sm" />
                Normal
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-flex h-4 w-4 rounded border-2 border-amber-500 bg-amber-100 shadow-sm" />
                Sélectionné
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-flex h-4 w-4 rounded border-2 border-sky-500 bg-sky-100 shadow-sm" />
                Affecté (Enfant)
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-flex h-4 w-4 rounded border-2 border-rose-500 bg-rose-100 shadow-sm" />
                Affecté (Parent)
              </span>
            </div>

            <div className="flex items-center gap-3">
              {/* Reset sélection */}
              <button
                type="button"
                onClick={handleResetSelection}
                className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-600 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-800"
                title="Aucune sélection"
              >
                <RotateCcw className="h-4 w-4" />
                Reset
              </button>

              {/* Export PDF */}
              <button
                type="button"
                onClick={handleExportPdf}
                className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-700 shadow-sm transition hover:bg-emerald-100 hover:border-emerald-300"
                title="Télécharger le visuel actuel en PDF"
              >
                <FileDown className="h-4 w-4" />
                PDF
              </button>

              {/* Bouton Guide */}
              <button
                type="button"
                onClick={() => setIsGuideOpen(true)}
                className="flex items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm font-medium text-indigo-700 shadow-sm transition hover:bg-indigo-100 hover:border-indigo-300"
              >
                <BookOpen className="h-4 w-4" />
                Guide
              </button>

              {/* Sélecteur client */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setIsClientDropdownOpen(!isClientDropdownOpen)}
                  className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
                >
                  <Building2 className="h-4 w-4 text-slate-400" />
                  <span>{selectedClient?.name ?? 'Client'}</span>
                  <ChevronDown
                    className={`h-4 w-4 text-slate-400 transition-transform ${isClientDropdownOpen ? 'rotate-180' : ''}`}
                  />
                </button>
                {isClientDropdownOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setIsClientDropdownOpen(false)}
                      aria-hidden
                    />
                    <div className="absolute right-0 z-20 mt-1.5 w-52 rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
                      {clients.map((client) => (
                        <button
                          key={client.id}
                          type="button"
                          onClick={() => handleClientChange(client.id)}
                          className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition ${
                            selectedClientId === client.id
                              ? 'bg-indigo-50 text-indigo-700 font-medium'
                              : 'text-slate-700 hover:bg-slate-50'
                          }`}
                        >
                          {client.name}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* Onglets Vue Enfant / Parent */}
              <div className="flex rounded-lg border border-slate-200 bg-slate-50/50 p-0.5">
                <button
                  type="button"
                  onClick={() => setViewMode('enfant')}
                  className={`flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium transition ${
                    viewMode === 'enfant'
                      ? 'bg-white text-indigo-600 shadow-sm'
                      : 'text-slate-600 hover:text-slate-800'
                  }`}
                >
                  <GitBranch className="h-3.5 w-3.5" />
                  Enfant
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode('parent')}
                  className={`flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium transition ${
                    viewMode === 'parent'
                      ? 'bg-white text-indigo-600 shadow-sm'
                      : 'text-slate-600 hover:text-slate-800'
                  }`}
                >
                  <GitMerge className="h-3.5 w-3.5" />
                  Parent
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Contenu */}
      <main className="flex-1 overflow-y-auto px-6 py-3">
        <div className="max-w-full">
          {isLoading && (
            <p className="text-sm text-slate-500 mb-3">Chargement du catalogue d’actifs…</p>
          )}
          {error && !isLoading && (
            <p className="text-sm text-red-600 mb-3">Erreur: {error}</p>
          )}
          {!isLoading && !error && groups.map((group) => (
            <CatalogueActifsSection
              key={group.id}
              groupName={group.name}
              assets={group.assets}
              selectedAssetId={selectedAssetId}
              highlightedAssetIds={highlightedAssetIds}
              viewMode={viewMode}
              onAssetClick={handleAssetClick}
            />
          ))}
        </div>
      </main>

      {/* Modal Guide PRIsME — onglets : Vision, Actif & inventaire, Usage, Cadres */}
      {isGuideOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setIsGuideOpen(false)} aria-hidden />
          <div className="relative max-h-[90vh] w-full max-w-5xl overflow-hidden rounded-2xl bg-gradient-to-b from-slate-900 to-slate-800 shadow-2xl ring-1 ring-indigo-500/30 border border-indigo-500/20 flex flex-col">
            {/* Header */}
            <div className="shrink-0 flex items-center justify-between border-b border-indigo-500/30 bg-gradient-to-r from-indigo-900/80 to-slate-900/80 px-8 py-5">
              <div className="flex items-center gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 text-white shadow-lg shadow-indigo-500/30">
                  <BookOpen className="h-6 w-6" />
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-indigo-200 to-purple-200 tracking-tight">
                      Guide — Catalogue des actifs
                    </h2>
                    <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-500/30 border border-purple-400/50 text-purple-200" title="Disponible dans LEX">
                      <Brain className="w-3.5 h-3.5 opacity-90" />
                      <span className="text-[10px] font-medium">AI</span>
                    </span>
                  </div>
                  <p className="text-sm text-indigo-200/90 mt-0.5">Approche holistique et pédagogique</p>
                  {catalogueActifsSyncDate && (
                    <div className="flex items-center gap-1.5 mt-2 text-xs text-indigo-300/90">
                      <Clock className="w-3.5 h-3.5" />
                      <span>Dernière synchro LEX: {formatLastAnalysisDate(catalogueActifsSyncDate)}</span>
                    </div>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsGuideOpen(false)}
                className="rounded-xl p-2.5 text-slate-400 hover:bg-white/10 hover:text-white transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Onglets */}
            <div className="shrink-0 flex gap-1 border-b border-indigo-500/30 bg-slate-900/50 px-6 py-2">
              {[
                { id: 'vision' as const, label: 'Vision & Pilier BZ', icon: Target },
                { id: 'servo' as const, label: 'SERVO & flux', icon: Database },
                { id: 'actif' as const, label: 'Actif TI & inventaire', icon: Layers },
                { id: 'usage' as const, label: 'Utilisation', icon: MousePointer },
                { id: 'cadres' as const, label: 'Cadres', icon: BookMarked },
              ].map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setGuideTab(id)}
                  className={`flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium transition ${guideTab === id ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:bg-white/5 hover:text-slate-200'}`}
                >
                  <Icon className="h-4 w-4" />
                  {label}
                </button>
              ))}
            </div>

            {/* Contenu par onglet */}
            <div className="flex-1 overflow-y-auto px-8 py-6 text-slate-200" style={{ maxHeight: 'calc(90vh - 200px)' }}>
              {guideTab === 'vision' && (
                <>
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-indigo-300 mb-3">Cadre référentiel BZ — Pilier 2</h3>
                  <p className="text-slate-300 text-sm leading-relaxed mb-4">
                    Le <strong className="text-white">deuxième pilier</strong> du cadre référentiel BZ s’appelle <strong className="text-amber-200">« Gestion des actifs & parties prenantes »</strong>. Il vise à maîtriser le <strong className="text-white">« qui a quoi »</strong> et à assurer la conformité sécurité. Il comporte deux volets : <strong className="text-white">actifs TI</strong> (inventaire, catalogue, dépendances) et <strong className="text-white">parties prenantes / employés</strong> (qui utilise quoi, accès, désactivation de comptes, conformité Loi 25 / RGPD). La page <strong className="text-white">Catalogue des actifs</strong> dans PRIsME est la partie <strong className="text-amber-200">actifs</strong> du pilier : tout le patrimoine TI et les dépendances entre actifs y sont visualisés. La partie employés/parties prenantes est gérée ailleurs (SERVO, RH, gestion des accès). Ce pilier s’alimente depuis <strong className="text-white">SERVO</strong> pour les actifs : documentation client, groupes et dépendances sont gérés dans SERVO ; le catalogue PRIsME affiche cette donnée de façon visuelle pour la gouvernance, les incidents, le budget et la décision.
                  </p>
                  <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-6 py-4 mb-6">
                    <p className="text-sm font-semibold text-amber-200 leading-snug">
                      L’inventaire des actifs TI est la base : sécurité, budget et décision s’appuient sur ce qu’on connaît. Sans inventaire fiable, pas de périmètre clair pour la cybersécurité, pas d’arbitrage budgétaire solide, pas de décision éclairée.
                    </p>
                  </div>
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-indigo-300 mb-2 mt-5">PRIsME MASTER vs PRIsME client</h3>
                  <ul className="text-slate-300 text-sm space-y-2 mb-4">
                    <li>· <strong className="text-white">PRIsME MASTER</strong> — Vue « tous les clients » : le catalogue affiche les actifs de <strong className="text-white">tous les clients</strong> BZ. Permet à la direction TI et au support de voir l’ensemble du patrimoine, de comparer et de prioriser entre clients.</li>
                    <li>· <strong className="text-white">PRIsME client</strong> — Vue « un client » : chaque instance PRIsME client (ex. PRIsME Légalis, PRIsME BZ inc.) affiche uniquement le <strong className="text-white">catalogue du client</strong> concerné. Le client ou le gestionnaire de compte ne voit que ses actifs et dépendances.</li>
                  </ul>
                  <p className="text-slate-300 text-sm leading-relaxed mb-4">
                    Ce catalogue vise une <strong className="text-white">vue claire et actionnable</strong> du patrimoine TI et des dépendances. Objectif : rendre <strong className="text-white">l’impact visible</strong> — panne, investissement, changement — pour une gouvernance TI rigoureuse et des décisions assumées.
                  </p>
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-indigo-300 mb-2 mt-5">Impact visible, concrètement</h3>
                  <ul className="text-slate-300 text-sm space-y-1.5 mb-4">
                    <li>· <strong className="text-white">Incident</strong> — Qui est touché si cet actif tombe ? (vue Parent)</li>
                    <li>· <strong className="text-white">Changement</strong> — Sur quoi s’appuie cet actif ? Quoi préparer ? (vue Enfant)</li>
                    <li>· <strong className="text-white">Budget</strong> — Quoi renouveler, quoi licencier, quel TCO ? (inventaire + liens)</li>
                  </ul>
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-indigo-300 mb-2 mt-5">Pour qui</h3>
                  <p className="text-slate-300 text-sm leading-relaxed mb-4">
                    Direction (vue d’ensemble, arbitrages), TI (exploitation, projets), Sécurité (périmètre, conformité), Finances (coûts, renouvellements). Une seule source de vérité partagée, alimentée par SERVO et affichée dans PRIsME.
                  </p>
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-indigo-300 mb-2 mt-5">Esprit PRIsME</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">
                    <strong className="text-white">Rigueur</strong> — Inventaire à jour et dépendances documentées dans SERVO. <strong className="text-white">Clarté</strong> — Visuel lisible dans PRIsME, pas de flou. <strong className="text-white">Holistique</strong> — Métier, apps, infra, sécurité et cadres alignés ; MASTER et clients cohérents.
                  </p>
                </>
              )}

              {guideTab === 'servo' && (
                <>
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-indigo-300 mb-3">SERVO — Application opérationnelle TI de BZ</h3>
                  <p className="text-slate-300 text-sm leading-relaxed mb-4">
                    <strong className="text-white">SERVO</strong> est l’application opérationnelle TI de BZ utilisée par le département TI au quotidien. C’est l’<strong className="text-white">ERP complet du département TI</strong> : toute la donnée métier TI (documentation, tickets, temps, knowledge base, rendement) y est centralisée. Le catalogue des actifs PRIsME est la partie <strong className="text-white">actifs</strong> du Pilier 2 « Gestion des actifs & parties prenantes » du cadre référentiel BZ (le pilier inclut aussi les employés/parties prenantes — « qui a quoi »). <strong className="text-amber-200">Tout le catalogue des actifs PRIsME s’alimente depuis SERVO</strong> : pas de saisie en double ; la source de vérité pour les actifs, les groupes et les dépendances est SERVO.
                  </p>
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-indigo-300 mb-3">Modules et fonctions SERVO</h3>
                  <ul className="text-slate-300 text-sm space-y-2 mb-4">
                    <li>· <strong className="text-white">Documentation technique</strong> — Documentation TI par client, par environnement, par actif.</li>
                    <li>· <strong className="text-white">Gestion des tickets</strong> — Gestion des tickets au quotidien pour <strong className="text-white">tous les clients</strong> ; suivi des incidents, demandes et changements.</li>
                    <li>· <strong className="text-white">Log de temps</strong> — Saisie et suivi du temps passé par client, par projet, par activité.</li>
                    <li>· <strong className="text-white">Knowledge base client</strong> — Base de connaissances par client (procédures, FAQ, runbooks).</li>
                    <li>· <strong className="text-white">Rapports de rendement</strong> — Rapports de rendement TI (SLA, disponibilité, volumes, etc.).</li>
                  </ul>
                  <p className="text-slate-400 text-sm leading-relaxed mb-5">
                    SERVO couvre donc l’ensemble du cycle opérationnel TI : documenter, ticketer, chronométrer, capitaliser et mesurer. Le catalogue des actifs en est une sortie structurée pour la gouvernance et la visualisation dans PRIsME.
                  </p>
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-indigo-300 mb-3">Documentation client et « Affiché dans catalogue »</h3>
                  <p className="text-slate-300 text-sm leading-relaxed mb-4">
                    Dans SERVO, la <strong className="text-white">documentation client</strong> permet de décrire les actifs TI de chaque client (applications, serveurs, services, etc.). Pour qu’un actif apparaisse dans le <strong className="text-white">Catalogue des actifs PRIsME</strong>, il faut dans SERVO cocher l’option <strong className="text-amber-200">« Affiché dans catalogue »</strong> (ou équivalent) sur cet actif. On associe alors l’actif à un <strong className="text-white">groupe</strong> (ex. Fonctions opérationnelles, Applications, Matériels, Sécurité). Les groupes servent à organiser le catalogue en sections visuelles (blocs par catégorie).
                  </p>
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-indigo-300 mb-3">Groupes et ordre des groupes</h3>
                  <p className="text-slate-300 text-sm leading-relaxed mb-4">
                    Les <strong className="text-white">groupes</strong> (catégories d’actifs) sont eux aussi créés et gérés dans <strong className="text-white">cette section SERVO</strong> dédiée au catalogue. C’est là qu’on définit la liste des groupes (ex. Fonctions opérationnelles, Applications et services, Matériels utilisateurs, Environnements cloud, Serveurs et services, Sécurité, Réseau local, Matériel serveurs) et surtout <strong className="text-amber-200">l’ordre d’affichage des groupes</strong> dans PRIsME. L’ordre défini dans SERVO est respecté dans le catalogue PRIsME pour une lecture cohérente (du métier vers l’infra, ou selon la logique BZ).
                  </p>
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-indigo-300 mb-3">Dépendances entre actifs</h3>
                  <p className="text-slate-300 text-sm leading-relaxed mb-4">
                    Les <strong className="text-white">dépendances entre actifs</strong> (qui dépend de quoi) sont également définies dans <strong className="text-white">cette section SERVO</strong>. Pour chaque actif, on renseigne les actifs dont il dépend (dépendances « Enfant ») et/ou les actifs qui dépendent de lui (dépendances « Parent »). Ces liens alimentent directement les vues Enfant et Parent du catalogue PRIsME : au clic sur un actif, les actifs liés s’affichent en bleu (Enfant) ou en rose (Parent). Une seule source de vérité : SERVO pour la donnée, PRIsME pour la visualisation et la gouvernance.
                  </p>
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-indigo-300 mb-3">Résumé du flux</h3>
                  <ul className="text-slate-300 text-sm space-y-1.5">
                    <li>1. Dans SERVO : documentation client → actifs, groupes, ordre des groupes, dépendances.</li>
                    <li>2. Coche « Affiché dans catalogue » + association à un groupe pour chaque actif à afficher.</li>
                    <li>3. PRIsME (MASTER ou client) lit les données SERVO et affiche le catalogue visuel.</li>
                    <li>4. Pas de saisie des actifs ou des dépendances dans PRIsME : tout vient de SERVO.</li>
                  </ul>
                </>
              )}

              {guideTab === 'actif' && (
                <>
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-indigo-300 mb-3">Source des données — SERVO</h3>
                  <p className="text-slate-300 text-sm leading-relaxed mb-4">
                    Les actifs affichés dans le catalogue PRIsME proviennent de <strong className="text-white">SERVO</strong> (application opérationnelle TI de BZ). La liste des actifs, les groupes (catégories), l’ordre des groupes et les dépendances entre actifs sont gérés dans SERVO ; PRIsME affiche uniquement cette donnée en vue catalogue. Pour ajouter, modifier ou supprimer un actif ou une dépendance, il faut le faire dans SERVO (documentation client, section catalogue).
                  </p>
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-indigo-300 mb-3">Définition</h3>
                  <p className="text-slate-300 text-sm leading-relaxed mb-2">
                    Un <strong className="text-white">actif TI</strong> est tout élément qui a de la valeur pour l’organisation et qui est nécessaire pour fournir ou supporter les services TI.
                  </p>
                  <p className="text-slate-400 text-sm leading-relaxed mb-4">
                    Deux critères : <strong className="text-slate-300">valeur</strong> (métier, opérationnelle ou financière) et <strong className="text-slate-300">nécessité</strong> pour les services (directe ou en support). Sans inventaire, on ne sait pas ce qu’on protège, ce qu’on paie ni ce qu’on fait évoluer.
                  </p>
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-indigo-300 mb-3">Types couverts</h3>
                  <ul className="text-slate-300 text-sm space-y-2 mb-4">
                    <li>· <strong className="text-white">Fonctions métier</strong> — RH, Comptabilité, Ventes, Operations, Direction (consommatrices des services TI)</li>
                    <li>· <strong className="text-white">Applications & services</strong> — Courriel, ERP, CRM, portails, ticketing, partage de fichiers, téléphonie</li>
                    <li>· <strong className="text-white">Matériels</strong> — Postes, laptops, serveurs physiques, stockage, réseau (switch, câblage), imprimantes</li>
                    <li>· <strong className="text-white">Environnements</strong> — Cloud (IaaS, SaaS), hébergement, VMs, services managés</li>
                    <li>· <strong className="text-white">Sécurité</strong> — Firewall, MFA, sauvegardes, certificats SSL, VPN, EDR, antispam</li>
                  </ul>
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-indigo-300 mb-3">Interdépendances</h3>
                  <p className="text-slate-300 text-sm leading-relaxed mb-4">
                    Les actifs ne vivent pas seuls. Chaque actif <strong className="text-white">dépend d’autres</strong> (vue Enfant) et <strong className="text-white">en fait dépendre</strong> (vue Parent). Cartographier ces liens permet d’anticiper les pannes, prioriser les investissements, dimensionner les sauvegardes et répondre aux exigences (sécurité, continuité, budget, conformité).
                  </p>
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-indigo-300 mb-3">Pourquoi l’inventaire est incontournable</h3>
                  <ul className="text-slate-300 text-sm space-y-2.5">
                    <li className="flex gap-2"><span className="text-indigo-400 mt-0.5">●</span> <span><strong className="text-white">Sécurité</strong> — Périmètre clair pour cybersécurité, correctifs, gestion des accès et conformité (NIST, SOC, etc.). On ne protège bien que ce qu’on connaît.</span></li>
                    <li className="flex gap-2"><span className="text-indigo-400 mt-0.5">●</span> <span><strong className="text-white">Budget</strong> — Arbitrage renouvellements, licences et projets en connaissance de cause. TCO, coûts cachés et redondances visibles.</span></li>
                    <li className="flex gap-2"><span className="text-indigo-400 mt-0.5">●</span> <span><strong className="text-white">Décision</strong> — Impact d’un changement ou d’une panne visible en un coup d’œil. Moins de surprises, plus de décisions assumées.</span></li>
                    <li className="flex gap-2"><span className="text-indigo-400 mt-0.5">●</span> <span><strong className="text-white">Continuité</strong> — Points critiques, chaînes de dépendance et points uniques de défaillance pour plans de reprise (PRA/PCA) et gestion d’incidents.</span></li>
                  </ul>
                </>
              )}

              {guideTab === 'usage' && (
                <>
                  <p className="text-slate-400 text-sm leading-relaxed mb-4">
                    Le catalogue affiche les actifs et dépendances alimentés par <strong className="text-slate-300">SERVO</strong>. En PRIsME MASTER on peut choisir le client (dropdown) ; en PRIsME client le catalogue du client est affiché directement.
                  </p>
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-indigo-300 mb-3">Enchaînement</h3>
                  <ol className="text-slate-300 text-sm space-y-2 mb-5 list-decimal list-inside">
                    <li>Choisir le <strong className="text-white">client</strong> (dropdown en haut).</li>
                    <li>Choisir la vue <strong className="text-amber-200">Enfant</strong> ou <strong className="text-amber-200">Parent</strong> (onglets à côté du client).</li>
                    <li><strong className="text-white">Cliquer</strong> sur un actif : il reste en évidence (ambre) ; les actifs liés s’affichent en bleu (Enfant) ou rose (Parent). Second clic sur le même actif = désélection.</li>
                    <li>Utiliser <strong className="text-white">Reset</strong> pour repartir sans sélection.</li>
                  </ol>
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-indigo-300 mb-3">Légende des couleurs</h3>
                  <ul className="text-slate-300 text-sm space-y-1.5 mb-5">
                    <li><span className="inline-block w-3 h-3 rounded border-2 border-slate-400 bg-white mr-2 align-middle" /> Normal</li>
                    <li><span className="inline-block w-3 h-3 rounded border-2 border-amber-500 bg-amber-100 mr-2 align-middle" /> Sélectionné (actif sur lequel vous avez cliqué)</li>
                    <li><span className="inline-block w-3 h-3 rounded border-2 border-sky-500 bg-sky-100 mr-2 align-middle" /> Affecté (vue Enfant : actifs dont dépend le sélectionné)</li>
                    <li><span className="inline-block w-3 h-3 rounded border-2 border-rose-500 bg-rose-100 mr-2 align-middle" /> Affecté (vue Parent : actifs qui dépendent du sélectionné)</li>
                  </ul>
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-indigo-300 mb-3">Cas d’usage</h3>
                  <ul className="text-slate-300 text-sm space-y-2 mb-4">
                    <li>· <strong className="text-white">Incident</strong> — Cliquer sur l’actif en panne en vue Parent : voir tout ce qui est impacté (applications, métier). Prioriser la résolution.</li>
                    <li>· <strong className="text-white">Changement</strong> — Cliquer sur l’actif à modifier en vue Enfant : voir les prérequis (serveurs, réseau, licences). Préparer le plan.</li>
                    <li>· <strong className="text-white">Budget / renouvellement</strong> — Parcourir les actifs par catégorie ; utiliser Parent pour voir ce qu’un renouvellement (ex. firewall) impactera.</li>
                    <li>· <strong className="text-white">Audit / conformité</strong> — Inventaire visuel pour documenter le périmètre et les dépendances (sécurité, continuité).</li>
                  </ul>
                  <p className="text-slate-400 text-sm leading-relaxed">
                    <strong className="text-amber-200">Enfant</strong> = « ce dont ça dépend ». <strong className="text-amber-200">Parent</strong> = « ce qui en dépend ». Toujours dans le sens des flèches de dépendance.
                  </p>
                </>
              )}

              {guideTab === 'cadres' && (
                <>
                  {/* Section Cadre référentiel BZ */}
                  <div className="rounded-xl border border-indigo-500/30 bg-indigo-950/40 px-5 py-4 mb-6">
                    <h3 className="text-base font-bold text-indigo-200 mb-2">Cadre référentiel BZ</h3>
                    <p className="text-slate-300 text-sm leading-relaxed mb-3">
                      Le <strong className="text-white">cadre référentiel BZ</strong> est la méthodologie propriétaire de BZ pour évaluer et faire progresser la maturité technologique et organisationnelle des organisations. Il structure l’offre de services (audits, roadmaps, infogérance) et sert de <strong className="text-amber-200">référent commun</strong> entre tous les livrables PRIsME, SERVO et les équipes.
                    </p>
                    <p className="text-slate-400 text-sm leading-relaxed">
                      Il repose sur <strong className="text-white">7 piliers stratégiques</strong> et des tactiques concrètes, alignés sur les normes (ITIL, COBIT, NIST, ISO 27001). Chaque pilier se mesure (ASWAS → ASIS → TARGET → GAP) pour prioriser les investissements et les roadmaps.
                    </p>
                  </div>

                  <h4 className="text-xs font-semibold uppercase tracking-wider text-indigo-300/90 mb-2 mt-2">Les 7 piliers du Cadre BZ</h4>
                  <ul className="text-slate-300 text-sm space-y-1 mb-4">
                    <li>1. <strong className="text-white">Stratégie & Gouvernance TI</strong> — Connecter les TI à la stratégie d’affaires et mesurer la valeur perçue</li>
                    <li>2. <strong className="text-amber-200">Gestion des actifs & parties prenantes</strong> — Maîtriser le « qui a quoi » et assurer la conformité sécurité <span className="text-amber-300/80">← Catalogue des actifs (partie actifs)</span></li>
                    <li>3. <strong className="text-white">Gestion de projet</strong> — Transformer les idées en valeur mesurable avec gouvernance rigoureuse</li>
                    <li>4. <strong className="text-white">Gestion des Services TI</strong> — Livrer un service de qualité au quotidien selon normes ITIL</li>
                    <li>5. <strong className="text-white">Cybersécurité</strong> — Protéger l’organisation de manière proactive selon NIST</li>
                    <li>6. <strong className="text-white">Amélioration continue</strong> — Ne pas subir le changement, le créer activement</li>
                    <li>7. <strong className="text-white">Innovation & IA</strong> — Adopter l’IA de manière stratégique et éthique</li>
                  </ul>
                  <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-5 py-3 mb-5">
                    <p className="text-slate-200 text-sm leading-relaxed">
                      <strong className="text-amber-200">Pilier 2 — Gestion des actifs & parties prenantes</strong> : objectif stratégique = visibilité 360° sur tous les actifs TI et utilisateurs avec conformité sécuritaire totale. Deux volets : <strong className="text-white">actifs TI</strong> (inventaire, catalogue, dépendances → page Catalogue des actifs PRIsME, alimentée par SERVO) et <strong className="text-white">parties prenantes / employés</strong> (qui utilise quoi, accès, désactivation comptes, Loi 25 / RGPD → géré dans SERVO, RH, gestion des accès). Le catalogue PRIsME est donc la brique « actifs » du Pilier 2.
                    </p>
                  </div>

                  <h3 className="text-sm font-semibold uppercase tracking-wider text-indigo-300 mb-3 mt-6">Alignement normatif (ITIL, COBIT, NIST)</h3>
                  <p className="text-slate-400 text-sm leading-relaxed mb-5">
                    Le Catalogue des actifs est la partie <strong className="text-slate-300">actifs</strong> du Pilier 2 « Gestion des actifs & parties prenantes » du cadre référentiel BZ (le pilier couvre aussi les employés/parties prenantes). Alimenté par SERVO, l’inventaire des actifs avec dépendances soutient directement les exigences des cadres reconnus. Une seule base de vérité (SERVO) sert la gouvernance, la sécurité et la continuité ; PRIsME (MASTER ou client) en affiche la vue catalogue.
                  </p>
                  <div className="space-y-4">
                    <div className="rounded-xl border border-indigo-500/20 bg-indigo-950/30 px-6 py-4">
                      <h4 className="text-sm font-semibold text-indigo-200 mb-2">ITIL 4</h4>
                      <p className="text-slate-400 text-sm leading-relaxed mb-2">
                        <strong className="text-slate-300">Service Value System (SVS)</strong> — Les actifs et leur configuration sont au cœur de la chaîne de valeur. Le catalogue alimente la gestion de la configuration des services (CMS) et la compréhension des dépendances entre composants (CIs) et services.
                      </p>
                      <p className="text-slate-500 text-xs leading-relaxed">Pratiques concernées : Gestion des actifs, Gestion de la configuration des services.</p>
                    </div>
                    <div className="rounded-xl border border-indigo-500/20 bg-indigo-950/30 px-6 py-4">
                      <h4 className="text-sm font-semibold text-indigo-200 mb-2">COBIT 2019</h4>
                      <p className="text-slate-400 text-sm leading-relaxed mb-2">
                        <strong className="text-slate-300">APO09</strong> (Gérer les actifs) — Cycle de vie des actifs, inventaire et valeur. <strong className="text-slate-300">BAI09</strong> (Gérer les actifs) — Actifs solutions et infrastructure. <strong className="text-slate-300">DSS02</strong> (Gérer les services) — Connaissance des actifs pour la fourniture et le support. Le catalogue fournit la vue actifs et dépendances attendue par ces objectifs.
                      </p>
                      <p className="text-slate-500 text-xs leading-relaxed">Objectifs : gouvernance des actifs, alignement avec la stratégie et les risques.</p>
                    </div>
                    <div className="rounded-xl border border-indigo-500/20 bg-indigo-950/30 px-6 py-4">
                      <h4 className="text-sm font-semibold text-indigo-200 mb-2">NIST CSF (Cybersecurity Framework)</h4>
                      <p className="text-slate-400 text-sm leading-relaxed mb-2">
                        <strong className="text-slate-300">Identify (ID.AM)</strong> — Asset Management : inventaire des actifs (matériel, logiciel, données, personnel) et des dépendances. Première fonction du CSF : sans identification claire des actifs, les autres fonctions (Protect, Detect, Respond, Recover) manquent de périmètre.
                      </p>
                      <p className="text-slate-500 text-xs leading-relaxed">Subcatégorie ID.AM-1 : inventaire physique et logiciel ; le catalogue étend cela aux liens et à l’impact métier.</p>
                    </div>
                  </div>
                  <p className="text-slate-400 text-sm leading-relaxed mt-5 border-t border-indigo-500/20 pt-4">
                    <strong className="text-white">Approche holistique</strong> — Un inventaire fiable et des dépendances documentées constituent la base commune à ces cadres pour la sécurité, le budget, la continuité et la prise de décision. Le catalogue PRIsME est la partie <strong className="text-white">actifs</strong> du Pilier 2 « Gestion des actifs & parties prenantes » du cadre référentiel BZ (le pilier inclut aussi les employés/parties prenantes). Alimenté par SERVO, le catalogue est pensé pour servir gouvernance, incidents et budget en une seule vue. PRIsME MASTER affiche le catalogue de tous les clients ; PRIsME client affiche le catalogue du client concerné.
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
